/**
 * Accessibility preferences, applied at BOOT (q-p2triage-fe-player-trust-and-ux).
 * The Accessibility panel saved the palette / reduce-motion / high-contrast / text
 * scale to localStorage, but only its own effect applied them — so every page load
 * ignored them until the player happened to reopen that panel. `main.tsx` now
 * applies the stored prefs before the first render; the panel uses the same
 * functions. Storage keys are unchanged. Duck-typed ports so it runs under
 * `node --test` with no DOM.
 */

export interface A11yPrefs {
  palette: string;
  reduceMotion: boolean;
  highContrast: boolean;
  /** Page text scale, percent (the panel's slider is 80–160). */
  textScale: number;
}

export const A11Y_KEYS = {
  palette: 'pxl.palette',
  reduceMotion: 'pxl.reduceMotion',
  highContrast: 'pxl.highContrast',
  textScale: 'pxl.a11yScale',
} as const;

const SCALE_MIN = 80;
const SCALE_MAX = 160;

/** Read the stored prefs; anything missing or malformed falls back to the default. */
export function readA11yPrefs(storage: Pick<Storage, 'getItem'>): A11yPrefs {
  const scale = Number(storage.getItem(A11Y_KEYS.textScale));
  return {
    palette: storage.getItem(A11Y_KEYS.palette) || 'default',
    reduceMotion: storage.getItem(A11Y_KEYS.reduceMotion) === '1',
    highContrast: storage.getItem(A11Y_KEYS.highContrast) === '1',
    textScale: Number.isFinite(scale) && scale >= SCALE_MIN && scale <= SCALE_MAX ? scale : 100,
  };
}

/** The parts of `document.documentElement` this writes. */
export interface A11yRoot {
  dataset: Record<string, string | undefined>;
  classList: { toggle(token: string, force?: boolean): unknown };
  style: { fontSize: string };
}

export function applyA11yPrefs(root: A11yRoot, prefs: A11yPrefs): void {
  root.dataset.palette = prefs.palette;
  root.classList.toggle('pxl-reduce-motion', prefs.reduceMotion);
  root.classList.toggle('pxl-high-contrast', prefs.highContrast);
  root.style.fontSize = `${prefs.textScale}%`;
}

/** Boot: apply whatever the player saved last time. Never throws (storage may be blocked). */
export function applyStoredA11yPrefs(): void {
  try {
    if (typeof document === 'undefined' || typeof localStorage === 'undefined') return;
    applyA11yPrefs(document.documentElement, readA11yPrefs(localStorage));
  } catch {
    // Storage access refused (privacy mode): defaults stand.
  }
}
